import { supabase } from '../lib/supabase';
import openaiService from './openaiService';

// Helper to validate UUID format
const isUUID = (str) => {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(str);
};

class ChatService {
  // Generate a unique ID for client-side use
  generateId() {
    return crypto.randomUUID();
  }

  // Generate a chat title from the first user message
  generateChatTitle(messages) {
    const firstUserMessage = messages.find(msg => msg.type === 'user');
    if (firstUserMessage) {
      const content = firstUserMessage.content;
      return content.length > 50 ? content.substring(0, 50) + '...' : content;
    }
    return 'New Chat';
  }

  // Generate an AI-powered title for a chat (max 4 words)
  async generateAIChatTitle(firstUserMessage) {
    try {
      if (!firstUserMessage || !firstUserMessage.trim()) {
        return 'New Chat';
      }

      console.log('🔍 [generateAIChatTitle] Generating title for message:', firstUserMessage.substring(0, 100));

      const systemPrompt = `You are a helpful assistant that creates concise, descriptive titles for chat conversations.

Rules:
- Create a title that captures the main topic or question from the user's message
- Maximum 4 words
- Be specific and descriptive
- Use title case
- No quotes, no punctuation at the end
- Examples: "Sales Strategy Planning", "Cold Email Optimization", "Fitness Routine Design", "Product Launch Ideas"

Return ONLY the title, nothing else.`;

      const response = await openaiService.createChatCompletion([
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: `Create a title for this chat conversation:\n\n${firstUserMessage}`
        }
      ], {
        model: 'gpt-4o-mini',
        temperature: 0.7,
        max_tokens: 20
      });

      if (response.error || !response.data) {
        console.warn('⚠️ [generateAIChatTitle] AI title generation failed, using fallback');
        // Fallback: use first 4 words of the message
        const words = firstUserMessage.trim().split(/\s+/).slice(0, 4).join(' ');
        return words || 'New Chat';
      }

      // Clean up the response - remove quotes, extra whitespace, etc.
      let title = response.data.trim();
      title = title.replace(/^["']|["']$/g, ''); // Remove surrounding quotes
      title = title.replace(/\.$/, ''); // Remove trailing period
      title = title.trim();

      // Ensure it's max 4 words
      const words = title.split(/\s+/);
      if (words.length > 4) {
        title = words.slice(0, 4).join(' ');
      }

      // If title is too short or empty, use fallback
      if (!title || title.length < 3) {
        const fallbackWords = firstUserMessage.trim().split(/\s+/).slice(0, 4).join(' ');
        return fallbackWords || 'New Chat';
      }

      console.log(`✅ [generateAIChatTitle] Generated title: "${title}"`);
      return title;
    } catch (error) {
      console.error('❌ [generateAIChatTitle] Error:', error);
      // Fallback: use first 4 words of the message
      const words = firstUserMessage?.trim().split(/\s+/).slice(0, 4).join(' ') || 'New Chat';
      return words;
    }
  }

  // ============================================
  // FOLDER METHODS (Ask Swirl chat folders)
  // ============================================

  // Get all user folders (chat folders only, NOT collections/topics)
  async getUserFolders(userId) {
    try {
      console.log(`🔍 [getUserFolders] Loading folders for user: ${userId}`);
      
      // Query all folders for this user - no filters
      const { data, error } = await supabase
        .schema('public')
        .from('folders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ [getUserFolders] Error loading folders:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log(`✅ [getUserFolders] Loaded ${data?.length || 0} folders`);
      if (data && data.length > 0) {
        console.log('📁 Sample folder IDs:', data.slice(0, 5).map(f => f.id));
        console.log('📁 Folder names:', data.slice(0, 5).map(f => f.name));
      } else {
        console.warn('⚠️ [getUserFolders] No folders found in database for user:', userId);
        // Let's also check if there are any folders at all in the table
        const { count, error: countError } = await supabase
          .schema('public')
          .from('folders')
          .select('*', { count: 'exact', head: true });
        if (!countError) {
          console.log(`📊 Total folders in database (all users): ${count || 0}`);
        }
      }
      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ [getUserFolders] Error:', error);
      return { data: null, error };
    }
  }

  // Create a new folder
  async createFolder(userId, folderData) {
    try {
      console.log(`🔍 [createFolder] Creating folder: ${folderData.name}`);
      const { data, error } = await supabase
        .schema('public')
        .from('folders')
        .insert({
          user_id: userId,
          name: folderData.name
        })
        .select()
        .single();

      if (error) throw error;
      console.log(`✅ [createFolder] Created folder: ${data.name}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [createFolder] Error creating folder:', error);
      return { data: null, error };
    }
  }

  // Rename a folder
  async renameFolder(userId, folderId, newName) {
    try {
      console.log(`🔍 [renameFolder] Renaming folder ${folderId} to: ${newName}`);
      const { data, error } = await supabase
        .schema('public')
        .from('folders')
        .update({ name: newName })
        .eq('user_id', userId)
        .eq('id', folderId)
        .select()
        .single();

      if (error) throw error;
      console.log(`✅ [renameFolder] Renamed folder: ${data.name}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [renameFolder] Error renaming folder:', error);
      return { data: null, error };
    }
  }

  // Delete a folder
  async deleteFolder(userId, folderId) {
    try {
      console.log(`🔍 [deleteFolder] Deleting folder: ${folderId}`);
      
      // First, move all chats in this folder to no folder (set folder_id to null)
      await supabase
        .schema('public')
        .from('conversations')
        .update({ folder_id: null })
        .eq('user_id', userId)
        .eq('folder_id', folderId);

      // Then delete the folder
      const { error } = await supabase
        .schema('public')
        .from('folders')
        .delete()
        .eq('user_id', userId)
        .eq('id', folderId);

      if (error) throw error;
      console.log(`✅ [deleteFolder] Deleted folder: ${folderId}`);
      return { data: true, error: null };
    } catch (error) {
      console.error('❌ [deleteFolder] Error deleting folder:', error);
      return { data: null, error };
    }
  }

  // ============================================
  // CONVERSATION METHODS
  // ============================================

  // Create a new chat conversation (empty, no messages yet)
  async createNewChat(userId, folderId = null, title = null, focusId = null) {
    try {
      console.log(`🔍 [createNewChat] Creating new conversation for user: ${userId}, folder: ${folderId || 'none'}, focus: ${focusId || 'none'}`);
      
      const conversationId = this.generateId();
      const { data: conversation, error } = await supabase
        .schema('public')
        .from('conversations')
        .insert({
          id: conversationId,
          user_id: userId,
          title: title || 'New Chat',
          folder_id: folderId || null,
          focus_id: focusId || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('❌ [createNewChat] Error creating conversation:', error);
        throw error;
      }

      console.log(`✅ [createNewChat] Created conversation: ${conversation.id}`);
      return { data: conversation, error: null };
    } catch (error) {
      console.error('❌ [createNewChat] Error:', error);
      return { data: null, error };
    }
  }

  // Save a chat conversation
  async saveChat(userId, chatData) {
    try {
      console.log(`🔍 [saveChat] Saving chat ${chatData.id} for user: ${userId}`);
      
      // First, save/update the conversation
      const conversationId = chatData.id || this.generateId();
      const { data: conversation, error: convError } = await supabase
        .schema('public')
        .from('conversations')
        .upsert({
          id: conversationId,
          user_id: userId,
          title: chatData.title || this.generateChatTitle(chatData.messages || []),
          folder_id: chatData.folder_id || null, // Use folder_id, not topic_id or project_id
          focus_id: chatData.focus_id || null, // Link to Focus if provided
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (convError) {
        console.error('❌ [saveChat] Error saving conversation:', convError);
        throw convError;
      }

      console.log('✅ [saveChat] Saved conversation:', conversation);

      // Then, save messages to chat_messages table
      if (chatData.messages && Array.isArray(chatData.messages) && chatData.messages.length > 0) {
        console.log(`🔍 [saveChat] Saving ${chatData.messages.length} messages to chat_messages table`);
        
        // Map messages to chat_messages format
        // CRITICAL: Always save attachments in metadata, even if empty array
        // This ensures attachments are preserved BEFORE the insert happens
        const messagesToSave = chatData.messages.map((msg, index) => {
          // Ensure attachments is always an array (never null/undefined)
          const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
          
          const referencedItems = msg.type === 'ai' && Array.isArray(msg.referencedItems) && msg.referencedItems.length > 0
            ? msg.referencedItems
            : undefined;
          
          // Test C: Validate UUID before database insert
          const isValidUUID = typeof msg.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(msg.id);
          if (!isValidUUID) {
            console.error(`❌ [TEST C FAILED] Message ${index} has invalid UUID:`, {
              id: msg.id,
              idType: typeof msg.id,
              isNumber: typeof msg.id === 'number',
              message: 'This message will fail database insert!'
            });
          } else {
            console.log(`✅ [TEST C PASSED] Message ${index} has valid UUID:`, msg.id);
          }
          
          // Ensure no numeric conversion happens
          if (typeof msg.id === 'number') {
            console.error(`❌ [TEST C CRITICAL] Message ${index} has numeric ID! This should not happen:`, msg.id);
          }
          
          console.log(`💾 [saveChat] Message ${index} (${msg.type}):`, {
            id: msg.id,
            idType: typeof msg.id,
            isValidUUID,
            contentLength: msg.content?.length || 0,
            attachmentsCount: attachments.length,
            referencedItemsCount: referencedItems?.length || 0,
            attachments: attachments.map(a => ({ id: a.id, file_name: a.file_name, hasThumbnail: !!a.thumbnail, hasFileUrl: !!a.file_url })),
            referencedItems: referencedItems
          });
          
          // Always include attachments in metadata, even if empty
          // Also save referencedItems for AI messages
          const metadata = {
            attachments: attachments,
            ...(referencedItems ? { referencedItems } : {})
          };
          
          return {
            id: msg.id, // Use UUID from message object - must be string UUID
            conversation_id: conversationId,
            user_id: userId,
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content || '',
            metadata: metadata, // Always save metadata with attachments field
            created_at: msg.timestamp ? new Date(msg.timestamp).toISOString() : new Date().toISOString()
          };
        });
        
        console.log(`💾 [saveChat] Prepared ${messagesToSave.length} messages to save. Total attachments:`, 
          messagesToSave.reduce((sum, m) => sum + (m.metadata?.attachments?.length || 0), 0));

        // Delete existing messages for this conversation first (to avoid duplicates)
        await supabase
          .schema('public')
          .from('chat_messages')
          .delete()
          .eq('conversation_id', conversationId);

        // Test C: Log insert payload before database insert
        console.log('🔍 [TEST C] Database insert payload validation:', {
          messageCount: messagesToSave.length,
          allHaveUUIDs: messagesToSave.every(m => typeof m.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(m.id)),
          ids: messagesToSave.map(m => ({ id: m.id, type: typeof m.id }))
        });
        
        // Insert new messages (attachments are already in metadata at this point)
        const { data: savedMessages, error: messagesError } = await supabase
          .schema('public')
          .from('chat_messages')
          .insert(messagesToSave)
          .select();
        
        // Test C: Validate insert result
        if (messagesError) {
          console.error('❌ [TEST C FAILED] Database insert error:', messagesError);
        } else if (savedMessages) {
          console.log('✅ [TEST C PASSED] Database insert successful:', {
            savedCount: savedMessages.length,
            savedIds: savedMessages.map(m => ({ id: m.id, type: typeof m.id }))
          });
        }

        if (messagesError) {
          console.error('❌ [saveChat] Error saving messages:', messagesError);
          console.error('Messages error details:', JSON.stringify(messagesError, null, 2));
        } else {
          console.log(`✅ [saveChat] Saved ${savedMessages?.length || 0} messages to chat_messages table`);
        }
      } else {
        console.log('⚠️ [saveChat] No messages to save');
      }

      return { data: conversation, error: null };
    } catch (error) {
      console.error('❌ [saveChat] Error:', error);
      return { data: null, error };
    }
  }

  // Get all user chats
  async getUserChats(userId, includeMessages = false) {
    try {
      console.log(`🔍 [getUserChats] Loading conversations for user: ${userId}, includeMessages: ${includeMessages}`);
      
      // Query conversations using folder_id (not topic_id)
      // Don't filter by archived or deleted - show all conversations
      const { data: conversations, error } = await supabase
        .schema('public')
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('❌ [getUserChats] Error loading conversations:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log(`✅ [getUserChats] Loaded ${conversations?.length || 0} conversations`);
      if (conversations && conversations.length > 0) {
        console.log('📋 Sample conversation IDs:', conversations.slice(0, 5).map(c => c.id));
      }

      // Load folder data and optionally messages for each conversation
      const conversationsWithFolders = await Promise.all(
        (conversations || []).map(async (conv) => {
          // Load folder if exists
          let folder = null;
          if (conv.folder_id) {
            const { data: folderData } = await supabase
              .schema('public')
              .from('folders')
              .select('id, name, created_at')
              .eq('id', conv.folder_id)
              .single();
            folder = folderData || null;
          }

          // Load messages if requested (for search)
          let messages = [];
          if (includeMessages) {
                    const { data: messagesData } = await supabase
                      .schema('public')
                      .from('chat_messages')
                      .select('id, role, content, metadata, created_at, conversation_id, user_id')
                      .eq('conversation_id', conv.id)
                      .order('created_at', { ascending: true });
            
            messages = (messagesData || []).map(msg => ({
              id: msg.id,
              type: msg.role === 'user' ? 'user' : 'ai',
              content: msg.content || '',
              timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
              // Always restore attachments from metadata, default to empty array if not present
              attachments: Array.isArray(msg.metadata?.attachments) ? msg.metadata.attachments : [],
            }));
          }

          return {
            ...conv,
            folder: folder,
            messages: messages,
          };
        })
      );

      return { data: conversationsWithFolders, error: null };
    } catch (error) {
      console.error('❌ [getUserChats] Error:', error);
      return { data: null, error };
    }
  }

  // Get a specific chat
  async getChat(userId, chatId) {
    try {
      console.log(`🔍 [getChat] Loading chat ${chatId} for user: ${userId}`);
      
      // 1. Load conversation (with folder_id)
      const { data: conversation, error } = await supabase
        .schema('public')
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .eq('id', chatId)
        .single();

      if (error) {
        console.error('❌ [getChat] Error loading conversation:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ [getChat] Loaded conversation:', conversation);

      // 2. Load messages from chat_messages (including metadata for attachments)
      console.log(`🔍 [getChat] Loading messages for conversation_id: ${chatId}`);
      const { data: messages, error: messagesError } = await supabase
        .schema('public')
        .from('chat_messages')
        .select('id, role, content, metadata, created_at, conversation_id, user_id')
        .eq('conversation_id', chatId)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('❌ [getChat] Error loading messages:', messagesError);
        console.error('Messages error details:', JSON.stringify(messagesError, null, 2));
      } else {
        console.log(`✅ [getChat] Loaded ${messages?.length || 0} messages from chat_messages table`);
      }

      // 3. Map chat_messages to expected format
      // CRITICAL: Always restore attachments from metadata, default to empty array if not present
      const formattedMessages = (messages || []).map((msg, index) => {
        const attachments = Array.isArray(msg.metadata?.attachments) ? msg.metadata.attachments : [];
        
        const referencedItems = msg.role === 'ai' && Array.isArray(msg.metadata?.referencedItems) 
          ? msg.metadata.referencedItems 
          : undefined;
        
        console.log(`📖 [getChat] Message ${index} (${msg.role}):`, {
          id: msg.id,
          contentLength: msg.content?.length || 0,
          metadata: msg.metadata,
          attachmentsCount: attachments.length,
          referencedItemsCount: referencedItems?.length || 0,
          attachments: attachments.map(a => ({ id: a.id, file_name: a.file_name, hasThumbnail: !!a.thumbnail, hasFileUrl: !!a.file_url })),
          referencedItems: referencedItems
        });
        
        return {
          id: msg.id,
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content || '',
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          // Always restore attachments from metadata, default to empty array if not present
          attachments: attachments,
          // Restore referencedItems for AI messages
          referencedItems: msg.role === 'ai' && Array.isArray(msg.metadata?.referencedItems) 
            ? msg.metadata.referencedItems 
            : undefined,
        };
      });
      
      console.log(`📖 [getChat] Restored ${formattedMessages.length} messages. Total attachments:`, 
        formattedMessages.reduce((sum, m) => sum + (m.attachments?.length || 0), 0));

      // 4. Load folder if it exists
      let folder = null;
      if (conversation.folder_id) {
        const { data: folderData } = await supabase
          .schema('public')
          .from('folders')
          .select('id, name, created_at')
          .eq('id', conversation.folder_id)
          .single();
        folder = folderData || null;
      }

      // 5. Return merged object
      return { 
        data: { 
          ...conversation, 
          folder,
          messages: formattedMessages,
          sources: [] // Sources might be in a separate table or not stored
        }, 
        error: null 
      };
    } catch (error) {
      console.error('❌ [getChat] Error:', error);
      return { data: null, error };
    }
  }

  // Delete a chat
  async deleteChat(userId, chatId) {
    try {
      // First, delete all messages associated with this conversation
      const { error: messagesError } = await supabase
        .schema('public')
        .from('chat_messages')
        .delete()
        .eq('conversation_id', chatId);

      if (messagesError) {
        console.error('Error deleting chat messages:', messagesError);
        throw messagesError;
      }

      // Then delete the conversation itself
      const { error } = await supabase
        .schema('public')
        .from('conversations')
        .delete()
        .eq('user_id', userId)
        .eq('id', chatId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting chat:', error);
      return { data: null, error };
    }
  }

  // Rename a chat
  async renameChat(userId, chatId, newTitle) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('conversations')
        .update({ title: newTitle })
        .eq('user_id', userId)
        .eq('id', chatId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error renaming chat:', error);
      return { data: null, error };
    }
  }

  // Toggle pin status of a chat (if column exists)
  async togglePinChat(userId, chatId, isPinned) {
    try {
      // Note: is_pinned column may not exist in schema
      const { data, error } = await supabase
        .schema('public')
        .from('conversations')
        .update({ is_pinned: isPinned })
        .eq('user_id', userId)
        .eq('id', chatId)
        .select()
        .single();

      if (error) {
        // If column doesn't exist, silently fail (feature not supported)
        if (error.code === '42703' || error.message?.includes('column') || error.message?.includes('does not exist')) {
          console.warn('⚠️ [togglePinChat] is_pinned column does not exist, action not supported');
          return { data: null, error: new Error('is_pinned column does not exist') };
        }
        throw error;
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error toggling pin chat:', error);
      return { data: null, error };
    }
  }

  // Archive a chat (delete since is_archived doesn't exist)
  async archiveChat(userId, chatId) {
    try {
      console.warn('⚠️ [archiveChat] is_archived column does not exist, deleting conversation instead');
      return await this.deleteChat(userId, chatId);
    } catch (error) {
      console.error('Error archiving chat:', error);
      return { data: null, error };
    }
  }

  // Move chat to folder
  async moveChatToFolder(userId, chatId, folderId) {
    try {
      console.log(`🔍 [moveChatToFolder] Moving chat ${chatId} to folder ${folderId || 'null (no folder)'}`);
      const { data, error } = await supabase
        .schema('public')
        .from('conversations')
        .update({ folder_id: folderId || null }) // Use folder_id, allow null to move back to no folder
        .eq('user_id', userId)
        .eq('id', chatId)
        .select()
        .single();

      if (error) {
        console.error('❌ [moveChatToFolder] Supabase error:', error);
        throw error;
      }
      
      console.log(`✅ [moveChatToFolder] Moved chat to folder ${folderId || 'null (no folder)'}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [moveChatToFolder] Error moving chat to folder:', error);
      return { data: null, error };
    }
  }

  // ============================================
  // FOCUS METHODS
  // ============================================

  // Get all focuses for a user
  async getUserFocuses(userId) {
    try {
      console.log(`🔍 [getUserFocuses] Loading focuses for user: ${userId}`);
      
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('❌ [getUserFocuses] Error loading focuses:', error);
        throw error;
      }

      console.log(`✅ [getUserFocuses] Loaded ${data?.length || 0} focuses`);
      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ [getUserFocuses] Error:', error);
      return { data: null, error };
    }
  }

  // Create a new focus
  async createFocus(userId, focusData) {
    try {
      console.log(`🔍 [createFocus] Creating focus: ${focusData.title}`);
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .insert({
          user_id: userId,
          title: focusData.title,
          description: focusData.description || null,
          mission: focusData.mission || null,
          cover_image_url: focusData.cover_image_url || null
        })
        .select()
        .single();

      if (error) throw error;
      console.log(`✅ [createFocus] Created focus: ${data.title}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [createFocus] Error creating focus:', error);
      return { data: null, error };
    }
  }

  // Update a focus
  async updateFocus(userId, focusId, updates) {
    try {
      console.log(`🔍 [updateFocus] Updating focus ${focusId}`);
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .update(updates)
        .eq('user_id', userId)
        .eq('id', focusId)
        .select()
        .single();

      if (error) throw error;
      console.log(`✅ [updateFocus] Updated focus: ${data.title}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [updateFocus] Error updating focus:', error);
      return { data: null, error };
    }
  }

  // Delete a focus
  async deleteFocus(userId, focusId) {
    try {
      console.log(`🔍 [deleteFocus] Deleting focus: ${focusId}`);
      
      // First, unlink all conversations from this focus (set focus_id to null)
      await supabase
        .schema('public')
        .from('conversations')
        .update({ focus_id: null })
        .eq('user_id', userId)
        .eq('focus_id', focusId);

      // Then delete the focus
      const { error } = await supabase
        .schema('public')
        .from('focuses')
        .delete()
        .eq('user_id', userId)
        .eq('id', focusId);

      if (error) throw error;
      console.log(`✅ [deleteFocus] Deleted focus: ${focusId}`);
      return { data: true, error: null };
    } catch (error) {
      console.error('❌ [deleteFocus] Error deleting focus:', error);
      return { data: null, error };
    }
  }

  // Get a specific focus
  async getFocus(userId, focusId) {
    try {
      console.log(`🔍 [getFocus] Loading focus ${focusId} for user: ${userId}`);
      
      const { data, error } = await supabase
        .schema('public')
        .from('focuses')
        .select('*')
        .eq('user_id', userId)
        .eq('id', focusId)
        .single();

      if (error) {
        console.error('❌ [getFocus] Error loading focus:', error);
        throw error;
      }

      console.log('✅ [getFocus] Loaded focus:', data);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [getFocus] Error:', error);
      return { data: null, error };
    }
  }

  // Link a conversation to a focus
  async linkConversationToFocus(userId, conversationId, focusId) {
    try {
      console.log(`🔍 [linkConversationToFocus] Linking conversation ${conversationId} to focus ${focusId || 'null (unlink)'}`);
      const { data, error } = await supabase
        .schema('public')
        .from('conversations')
        .update({ focus_id: focusId || null })
        .eq('user_id', userId)
        .eq('id', conversationId)
        .select()
        .single();

      if (error) {
        console.error('❌ [linkConversationToFocus] Supabase error:', error);
        throw error;
      }
      
      console.log(`✅ [linkConversationToFocus] Linked conversation to focus ${focusId || 'null (unlinked)'}`);
      return { data, error: null };
    } catch (error) {
      console.error('❌ [linkConversationToFocus] Error linking conversation to focus:', error);
      return { data: null, error };
    }
  }

  // Get recent conversations for a focus
  async getFocusRecentConversations(userId, focusId, limit = 5) {
    try {
      console.log(`🔍 [getFocusRecentConversations] Loading recent conversations for focus ${focusId}`);
      
      // Use the RPC function we created in the migration
      const { data, error } = await supabase
        .rpc('rpc_get_focus_recent_conversations', {
          focus_id: focusId,
          result_limit: limit
        });

      if (error) {
        console.error('❌ [getFocusRecentConversations] RPC error:', error);
        throw error;
      }

      console.log(`✅ [getFocusRecentConversations] Loaded ${data?.length || 0} recent conversations`);
      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ [getFocusRecentConversations] Error:', error);
      return { data: null, error };
    }
  }

  // Search chats
  async searchChats(userId, query) {
    try {
      const { data: conversations, error } = await supabase
        .schema('public')
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .ilike('title', `%${query}%`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Load folders separately for each conversation
      const conversationsWithFolders = await Promise.all(
        (conversations || []).map(async (conv) => {
          if (!conv.folder_id) {
            return { ...conv, folder: null };
          }

          const { data: folder } = await supabase
            .schema('public')
            .from('folders')
            .select('id, name, created_at')
            .eq('id', conv.folder_id)
            .single();

          return {
            ...conv,
            folder: folder || null,
          };
        })
      );

      return { data: conversationsWithFolders, error: null };
    } catch (error) {
      console.error('Error searching chats:', error);
      return { data: null, error };
    }
  }

  // ============================================
  // PINNED MOMENTS METHODS
  // ============================================

  // Get all pinned moments for a user
  async getUserPinnedMoments(userId) {
    try {
      console.log(`🔍 [getUserPinnedMoments] Loading pinned moments for user: ${userId}`);
      
      const { data, error } = await supabase
        .schema('public')
        .from('pinned_moments')
        .select(`
          *,
          conversation:conversations(id, title)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist (404), return empty array instead of error
        if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
          console.warn('⚠️ [getUserPinnedMoments] pinned_moments table does not exist. Please run the migration.');
          return { data: [], error: null };
        }
        console.error('❌ [getUserPinnedMoments] Error loading pinned moments:', error);
        throw error;
      }

      console.log(`✅ [getUserPinnedMoments] Loaded ${data?.length || 0} pinned moments`);
      return { data: data || [], error: null };
    } catch (error) {
      // Handle 404 errors gracefully
      if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
        console.warn('⚠️ [getUserPinnedMoments] pinned_moments table does not exist. Please run the migration.');
        return { data: [], error: null };
      }
      console.error('❌ [getUserPinnedMoments] Error:', error);
      return { data: [], error };
    }
  }

  // Check if a specific message is pinned
  async isMessagePinned(userId, messageId) {
    // Skip invalid message IDs to prevent 400 errors
    if (!messageId || !isUUID(messageId)) {
      console.warn("[isMessagePinned] Skipping invalid messageId:", messageId);
      return { data: null, error: null };
    }

    try {
      const { data, error } = await supabase
        .schema('public')
        .from('pinned_moments')
        .select('id')
        .eq('user_id', userId)
        .eq('message_id', messageId)
        .maybeSingle();

      if (error) {
        // If table doesn't exist (404), return null instead of error
        if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
          return { data: null, error: null };
        }
        console.error('❌ [isMessagePinned] Error checking pinned status:', error);
        return { data: null, error };
      }

      return { data: data?.id || null, error: null };
    } catch (error) {
      // Handle 404 errors gracefully
      if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
        return { data: null, error: null };
      }
      console.error('❌ [isMessagePinned] Error:', error);
      return { data: null, error };
    }
  }

  // Pin a moment in a conversation
  async pinMoment(userId, conversationId, messageId, messageContent) {
    // Test E: Validate messageId is UUID before pinning
    const isValidUUID = typeof messageId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(messageId);
    if (!messageId || !isUUID(messageId)) {
      console.error('❌ [TEST E FAILED] Invalid messageId format:', {
        messageId,
        messageIdType: typeof messageId,
        isNumber: typeof messageId === 'number',
        isValidUUID
      });
      console.warn("[pinMoment] Skipping invalid messageId:", messageId);
      return { 
        data: null, 
        error: { message: 'Invalid message ID format. Must be a valid UUID.', code: 'INVALID_MESSAGE_ID' }
      };
    }

    try {
      console.log(`✅ [TEST E] Valid UUID messageId received:`, messageId);
      console.log(`🔍 [pinMoment] Pinning moment:`, {
        userId,
        conversationId,
        messageId,
        messageIdType: typeof messageId,
        isValidUUID,
        contentLength: messageContent?.length
      });

      // Generate a short title from the message content (max 4 words)
      const words = (messageContent || '').split(' ').slice(0, 4).join(' ');
      const title = words.length > 50 ? words.substring(0, 50) + '...' : words || 'Saved Moment';

      // Test E: Log insert payload
      const insertPayload = {
        user_id: userId,
        conversation_id: conversationId,
        message_id: messageId, // Already validated as UUID
        title: title,
        message_content: messageContent || ''
      };
      console.log('🔍 [TEST E] Pinned moment insert payload:', {
        ...insertPayload,
        messageIdType: typeof insertPayload.message_id,
        isValidUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(insertPayload.message_id)
      });

      const { data, error } = await supabase
        .schema('public')
        .from('pinned_moments')
        .insert(insertPayload)
        .select()
        .single();

      if (error) {
        // If table doesn't exist (404), provide helpful error message
        if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
          const errorMsg = 'The pinned_moments table does not exist. Please run the database migration: supabase/migrations/20250131000000_create_pinned_moments_table.sql';
          console.error('❌ [pinMoment]', errorMsg);
          return { 
            data: null, 
            error: { message: errorMsg, code: 'TABLE_NOT_FOUND' }
          };
        }
        console.error('❌ [pinMoment] Database error:', error);
        throw error;
      }

      console.log(`✅ [TEST E PASSED] Successfully pinned moment:`, {
        momentId: data.id,
        messageId: data.message_id,
        messageIdType: typeof data.message_id
      });
      return { data, error: null };
    } catch (error) {
      // Handle 404 errors gracefully
      if (error.code === 'PGRST116' || error.message?.includes('404') || error.message?.includes('does not exist')) {
        const errorMsg = 'The pinned_moments table does not exist. Please run the database migration.';
        console.error('❌ [pinMoment]', errorMsg);
        return { 
          data: null, 
          error: { message: errorMsg, code: 'TABLE_NOT_FOUND' }
        };
      }
      console.error('❌ [pinMoment] Error:', error);
      return { data: null, error };
    }
  }

  // Unpin a moment
  async unpinMoment(userId, momentId) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('pinned_moments')
        .delete()
        .eq('id', momentId)
        .eq('user_id', userId);

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('❌ [unpinMoment] Error:', error);
      return { data: null, error };
    }
  }

  // Rename a pinned moment
  async renamePinnedMoment(userId, momentId, newTitle) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('pinned_moments')
        .update({ title: newTitle })
        .eq('id', momentId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('❌ [renamePinnedMoment] Error:', error);
      return { data: null, error };
    }
  }

  // ============================================
  // LEGACY METHODS (for backward compatibility)
  // ============================================
  // These methods map old project/topic references to folders

  // Legacy: getUserProjects -> getUserFolders
  async getUserProjects(userId) {
    console.warn('⚠️ [getUserProjects] Deprecated: Use getUserFolders instead');
    return this.getUserFolders(userId);
  }

  // Legacy: createProject -> createFolder
  async createProject(userId, projectData) {
    console.warn('⚠️ [createProject] Deprecated: Use createFolder instead');
    return this.createFolder(userId, { name: projectData.name });
  }

  // Legacy: renameProject -> renameFolder
  async renameProject(userId, projectId, newName) {
    console.warn('⚠️ [renameProject] Deprecated: Use renameFolder instead');
    return this.renameFolder(userId, projectId, newName);
  }

  // Legacy: deleteProject -> deleteFolder
  async deleteProject(userId, projectId) {
    console.warn('⚠️ [deleteProject] Deprecated: Use deleteFolder instead');
    return this.deleteFolder(userId, projectId);
  }

  // Legacy: moveChatToProject -> moveChatToFolder
  async moveChatToProject(userId, chatId, projectId) {
    console.warn('⚠️ [moveChatToProject] Deprecated: Use moveChatToFolder instead');
    return this.moveChatToFolder(userId, chatId, projectId);
  }
}

export default new ChatService();
